import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  transactionsApi,
  type FreeAgentsQuery,
  type AddDropPayload,
  type SubmitWaiverPayload,
} from '@/api/transactions.api'
import type { Player, WaiverClaim, WaiverStatus, AddDropResult } from '@/types/domain'

const DEFAULT_PAGE_SIZE = 25

/**
 * Free agents + waivers for a single league. The backend owns every roster/waiver rule: this store
 * loads server-filtered free agents, sends add/drop + waiver-claim intents, and surfaces backend
 * errors (thrown to callers). `claims` is the caller's pending claims (or all, for a commissioner)
 * and is reloaded after mutations and on `WaiverProcessed` pushes (see the notification store).
 */
export const useWaiverStore = defineStore('waiver', () => {
  // Free-agent pool (paged, server-filtered).
  const freeAgents = ref<Player[]>([])
  const page = ref(1)
  const pageSize = ref(DEFAULT_PAGE_SIZE)
  const total = ref(0)
  const loading = ref(false)

  // Waiver claims (my pending, or all when commissioner).
  const claims = ref<WaiverClaim[]>([])
  const claimsLoading = ref(false)

  /** Authoritative free-agent refresh from REST. */
  async function loadFreeAgents(leagueId: string, query: FreeAgentsQuery = {}): Promise<void> {
    loading.value = true
    try {
      const result = await transactionsApi.getFreeAgents(leagueId, {
        pageSize: pageSize.value,
        ...query,
      })
      freeAgents.value = result.items
      page.value = result.page
      pageSize.value = result.pageSize
      total.value = result.total
    } finally {
      loading.value = false
    }
  }

  /**
   * Immediately add a free agent (dropping `dropPlayerId` if the roster is full). Errors
   * (`roster-full`, `player-not-free`, `player-on-waivers`, `drop-not-on-roster`, `invalid-roster`)
   * propagate to the caller for display. Returns the add/drop result.
   */
  async function addDrop(
    leagueId: string,
    teamId: string,
    payload: AddDropPayload,
  ): Promise<AddDropResult> {
    const result = await transactionsApi.addDrop(leagueId, teamId, payload)
    // The added player is no longer a free agent; drop it from the pool optimistically.
    freeAgents.value = freeAgents.value.filter((p) => p.id !== payload.addPlayerId)
    return result
  }

  /** Loads waiver claims (own, or all for a commissioner). Optional team/status filter. */
  async function loadClaims(
    leagueId: string,
    filter: { teamId?: string; status?: WaiverStatus } = {},
  ): Promise<void> {
    claimsLoading.value = true
    try {
      claims.value = await transactionsApi.listWaivers(leagueId, filter)
    } finally {
      claimsLoading.value = false
    }
  }

  /**
   * Submit a waiver claim. Errors (`player-not-on-waivers`, `duplicate-claim`, `roster-full`)
   * propagate. The new claim is prepended so the list reflects it without a round-trip.
   */
  async function submitWaiver(
    leagueId: string,
    payload: SubmitWaiverPayload,
  ): Promise<WaiverClaim> {
    const claim = await transactionsApi.submitWaiver(leagueId, payload)
    claims.value = [claim, ...claims.value]
    return claim
  }

  /** Cancel a pending claim. On success it is removed from the list. */
  async function cancelWaiver(leagueId: string, claimId: string): Promise<void> {
    await transactionsApi.cancelWaiver(leagueId, claimId)
    claims.value = claims.value.filter((c) => c.id !== claimId)
  }

  /**
   * Apply a `WaiverProcessed` push (from the notification hub): mark the matching claim's status.
   * The full list is re-pulled by the view for authoritative roster/priority changes.
   */
  function applyWaiverProcessed(claimId: string, status: WaiverStatus): void {
    const claim = claims.value.find((c) => c.id === claimId)
    if (claim) claim.status = status
  }

  function reset(): void {
    freeAgents.value = []
    page.value = 1
    total.value = 0
    claims.value = []
    loading.value = false
    claimsLoading.value = false
  }

  return {
    // state
    freeAgents,
    page,
    pageSize,
    total,
    loading,
    claims,
    claimsLoading,
    // actions
    loadFreeAgents,
    addDrop,
    loadClaims,
    submitWaiver,
    cancelWaiver,
    applyWaiverProcessed,
    reset,
  }
})
